import { Status } from '@/common/constants';
import { AccountSummaryDateFilter } from '@/types/api/reports.types';

/**
 * Converts a date string or Date object to an ISO string, optionally adjusting the time.
 *
 * @description
 * This function handles three main cases:
 * 1. Date strings in DD/MM/YYYY format (European style)
 * 2. Any value that can be parsed by the Date constructor
 * 3. Undefined values (returns an empty string)
 *
 * The function provides two optional adjustments:
 * - By default, it adds +2 hours to account for timezone differences (can be disabled with noHoursAdded)
 * - If endOfDay is true, it sets the time to 23:59:59.999 to represent the end of the day.
 *   This takes precedence over the +2 hour adjustment.
 *
 * @param {string | Date | undefined} value - The date to convert, either as:
 *   - A string in DD/MM/YYYY format (e.g., "25/12/2023")
 *   - A Date object
 *   - Any date string parsable by the Date constructor
 *   - undefined (returns an empty string)
 * @param {boolean} [noHoursAdded=false] - If true, skips the +2 hour adjustment. Defaults to false.
 * @param {boolean} [endOfDay=false] - If true, sets the time to 23:59:59.999 (overrides noHoursAdded). Defaults to false.
 *
 * @returns {string} The date in ISO 8601 format with the specified adjustments, or an empty string if value is undefined.
 */

export const convertToISOWithTimezone = (
  value: string | Date | undefined,
  noHoursAdded: boolean = false,
  endOfDay: boolean = false,
): string => {
  if (value === undefined) {
    return '';
  }

  let date: Date | undefined;
  if (typeof value === 'string') {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
    if (match) {
      const day = Number.parseInt(match[1] ?? '');
      const month = Number.parseInt(match[2] ?? '');
      const year = Number.parseInt(match[3] ?? '');
      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 9999) {
        date = new Date(year, month - 1, day);
      }
    }
  }
  date ??= new Date(value);

  if (endOfDay) {
    date.setHours(23, 59, 59, 999);
  } else if (!noHoursAdded) {
    date.setHours(date.getHours() + 2);
  }

  return date.toISOString();
};

/**
 * Checks if a date is after the start date.
 *
 * @param {string | undefined} value - The end date to check (in ISO format)
 * @param {string} start - The start date to compare against (in ISO format)
 * @returns {boolean} True if the value date is after the start date
 */
export const dateIsAfterStartDate = (value: string | undefined, start: string): boolean => {
  if (!value || !start) return false;

  const endDate = new Date(value);
  const startDate = new Date(start);

  return endDate > startDate;
};

export const formatDateAccounts = (inputDate: string): string => {
  const date = new Date(inputDate);

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  };

  return date.toLocaleDateString('en-US', options);
};

export const formatDateDMY = (value: string, pad: boolean = false): string => {
  const date = new Date(value);
  const day = pad ? date.getDate().toString().padStart(2, '0') : date.getDate().toString();
  const month = pad
    ? (date.getMonth() + 1).toString().padStart(2, '0')
    : (date.getMonth() + 1).toString();
  const year = date.getFullYear().toString();
  return `${day}/${month}/${year}`;
};

/** A date's month as `M/YY`, read in the platform's zone so it is the same month in every browser. */
export const formatDateMY = (value: string): string => {
  const [year, month] = platformDayKey(new Date(value)).split('-');
  return year && month ? `${Number(month)}/${year.slice(2)}` : '';
};

export const formatDateDM = (value: string): string => {
  const date = new Date(value);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  return `${day}/${month}`;
};

export const formatDateToAccountString = (isoDateString: string | Date): string => {
  const date = new Date(isoDateString);

  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString('default', { month: 'short' });
  const year = date.getFullYear();

  return `${day} ${month}, ${year}`;
};

export const getDateRanges = (
  dateRangeType: AccountSummaryDateFilter,
): {
  endDate: Date;
  startDate: Date;
} => {
  const today = new Date();

  const createUTCDate = (
    year: number,
    month: number,
    day: number,
    hour: number = 0,
    minute: number = 0,
    second: number = 0,
    millisecond: number = 0,
  ) => {
    return new Date(Date.UTC(year, month, day, hour, minute, second, millisecond));
  };

  // Current Month
  const currentMonthStartDate = createUTCDate(today.getFullYear(), today.getMonth(), 1);
  const currentMonthEndDate = createUTCDate(
    today.getFullYear(),
    today.getMonth() + 1,
    0,
    23,
    59,
    59,
    999,
  );

  // Last Month
  const lastMonthStartDate = createUTCDate(today.getFullYear(), today.getMonth() - 1, 1);

  // Last 3 Months
  const last3MonthsStartDate = createUTCDate(today.getFullYear(), today.getMonth() - 2, 1);

  switch (dateRangeType) {
    case 'currentMonth': {
      return {
        startDate: currentMonthStartDate,
        endDate: currentMonthEndDate,
      };
    }
    case 'lastMonth': {
      return {
        startDate: lastMonthStartDate,
        endDate: currentMonthEndDate,
      };
    }
    case 'last3Months': {
      return {
        startDate: last3MonthsStartDate,
        endDate: currentMonthEndDate,
      };
    }
    default: {
      throw new Error('Invalid date range type');
    }
  }
};

export const getFirstDayOfMonth = (date: string): Date => {
  const providedDate = new Date(date);
  const today = new Date();
  const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  if (providedDate >= firstDayOfCurrentMonth) {
    return providedDate;
  }

  const isTodayFirstOfMonth = today.getDate() === 1;
  return isTodayFirstOfMonth
    ? new Date(today.getFullYear(), today.getMonth() - 1, 1)
    : firstDayOfCurrentMonth;
};

export const getCurrentMonthDateRange = (): {
  startDate: string;
  endDate: string;
} => {
  const now = new Date();

  // Start of the month (first day at 00:00:00 UTC)
  const startDate = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0));

  // End of today (current day at 23:59:59.999 UTC)
  const endDate = new Date(
    Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999),
  );

  return { startDate: startDate.toISOString(), endDate: endDate.toISOString() };
};

/**
 * Calculates and formats the display text for a due or expiry date based on its status.
 * Handles different statuses, date formatting, and remaining time calculations with color hints.
 *
 * @param expiryDateStr - The expiry date in ISO format (e.g., "2025-05-22T00:00:00Z"). Can be null or undefined.
 * @param status - The status of the item (e.g., 'Expired', 'Pending', 'Draft').
 * @returns An object containing the display text and an optional CSS class for styling.
 * Example: { text: '14 Feb 2025', class: '' } or { text: '2 months remaining', class: 'text-red-500' }
 */
export const getDueDateDisplayText = (
  expiryDateStr: string | null | undefined,
  status: Status,
): {
  text: string;
  class: string;
} => {
  const output = {
    text: '',
    class: '',
  };

  const formatDateDisplay = (date: Date): string => {
    return formatDateToAccountString(date);
  };

  // Statuses that always show a dash
  if (status === Status.cancelled || status === Status.declined) {
    output.text = '-';
    return output;
  }

  if (!expiryDateStr) {
    output.text = '-';
    return output;
  }

  // Parse the expiry date and current date
  const expiryDate = new Date(expiryDateStr);
  const currentDate = new Date();

  // Create date-only versions, ensuring no time component interferes
  const expiryStartOfDay = new Date(
    expiryDate.getFullYear(),
    expiryDate.getMonth(),
    expiryDate.getDate(),
  );
  const currentStartOfDay = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    currentDate.getDate(),
  );

  // Calculate the difference in milliseconds between the start of days
  const diffMs = expiryStartOfDay.getTime() - currentStartOfDay.getTime();

  // Calculate the *exact* number of full days difference
  // Use division and check for negative to determine if it's in the past, today, or future.
  const daysDifference = diffMs / (1000 * 60 * 60 * 24);

  switch (status) {
    case Status.expired: {
      output.text = formatDateDisplay(expiryDate);
      output.class = 'text-gray-400';
      break;
    }

    case Status.approved: {
      output.text = formatDateDisplay(expiryDate);
      break;
    }

    case Status.pending:
    case Status.draft: {
      if (daysDifference < 0) {
        output.text = formatDateDisplay(expiryDate);
        output.class = 'text-red-500';
      } else if (daysDifference === 0) {
        output.text = '1 day remaining';
        output.class = 'text-red-500';
      } else {
        const displayDays = Math.round(daysDifference) + 1;

        if (displayDays <= 3) {
          output.text = `${displayDays} day${displayDays === 1 ? '' : 's'} remaining`;
          output.class = 'text-red-500';
        } else if (displayDays <= 31) {
          output.text = `${displayDays} days remaining`;
        } else {
          const diffMonths = Math.max(1, Math.floor(displayDays / 30));
          output.text = `${diffMonths} month${diffMonths === 1 ? '' : 's'} remaining`;
        }
      }
      break;
    }

    default: {
      output.text = formatDateDisplay(expiryDate);
      break;
    }
  }

  return output;
};

/**
 * Formats an ISO date string into a user-friendly date and time representation
 *
 * @param {string} dateStr - The ISO date string to format (e.g., "2025-04-11T10:49:33.714Z")
 * @param {boolean} includeTime - Whether to include the time in the output (default: true)
 * @param {boolean} includeOn - Whether to include the "on" prefix (default: true)
 * @returns {string} The formatted date string in the format "DD MMM YYYY" or "DD MMM YYYY at HH:MM"
 *
 * @example
 * // Returns "11 Apr 2025 at 10:49" (time may vary based on timezone)
 * getDateAndTimestamp("2025-04-11T10:49:33.714Z")
 *
 * @example
 * // Returns "11 Apr 2025" (date only)
 * getDateAndTimestamp("2025-04-11T10:49:33.714Z", false)
 *
 * @example
 * // Returns "on 11 Apr 2025 at 10:49" (with "on" prefix)
 * getDateAndTimestamp("2025-04-11T10:49:33.714Z", true)
 *
 * @example
 * // Returns "Invalid date" if the input cannot be parsed
 * getDateAndTimestamp("not-a-valid-date")
 */
export const getDateAndTimestamp = (
  dateStr: string,
  includeTime: boolean = true,
  includeOn: boolean = true,
): string => {
  // Create a new Date object from the input string
  const date = new Date(dateStr);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }

  // Define month names
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  // Extract date components
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  // Construct the date part
  const dateFormatted = `${day} ${month} ${year}`;

  // If time is not needed, return the date with optional "on" prefix
  if (!includeTime) {
    return includeOn ? `on ${dateFormatted}` : dateFormatted;
  }

  // Extract time components
  const hours = date.getHours();
  const minutes = date.getMinutes();

  // Format minutes with leading zero if needed
  const formattedMinutes = minutes.toString().padStart(2, '0');

  // Construct the final formatted string with time and optional "on" prefix
  const fullFormat = `${dateFormatted} at ${hours}:${formattedMinutes}`;
  return includeOn ? `on ${fullFormat}` : fullFormat;
};

/**
 * Represents a collection of ISO 8601 date strings.
 */
interface DateFields {
  deletedAt?: string | null; // ISO 8601 format (e.g. 2025-05-20T06:57:48.816Z)
  reviewedAt?: string | null; // ISO 8601 format (e.g. 2025-05-20T06:57:48.816Z)
  createdAt: string; // ISO 8601 format (e.g. 2025-05-20T06:57:48.816Z)
}

/**
 * Determines the last modified date based on the presence of deletedAt, reviewedAt, or createdAt.
 *
 * @param {DateFields} dates - An object containing the date properties in ISO 8601 format.
 * @returns {string} The formatted last modified date string.
 */
export const getLastModifiedDate = (dates: DateFields): string => {
  const { deletedAt, reviewedAt, createdAt } = dates;

  if (deletedAt) {
    return formatDateToAccountString(deletedAt);
  } else if (reviewedAt) {
    return formatDateToAccountString(reviewedAt);
  } else {
    return formatDateToAccountString(createdAt);
  }
};

/**
 * @typedef {Object} FormattedDateTimeParts
 * @property {string} datePart - The formatted date in "DD/MM/YY" format (e.g., "22/05/25").
 * @property {string} timePart - The formatted time in "HH:MM" format (e.g., "11:29").
 */

/**
 * Converts a UTC date-time string (e.g., "2025-05-22T11:29:51.782Z") into
 * two separate formatted parts: a date (DD/MM/YY) and a time (HH:MM).
 * The function uses UTC methods to ensure consistent parsing regardless of local time zone.
 *
 * @param {string} dateTimeString - The input date-time string in ISO 8601 format, preferably UTC.
 * @returns {FormattedDateTimeParts} An object containing the formatted datePart and timePart.
 * Returns empty strings for both if the input is null or undefined.
 */
export const getTwoPartDate = (dateTimeString: string) => {
  if (!dateTimeString) {
    return { datePart: '', timePart: '' };
  }

  const date = new Date(dateTimeString);

  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = String(date.getUTCFullYear()).slice(-2);

  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');

  const datePart = `${day}/${month}/${year}`;
  const timePart = `${hours}:${minutes}`;

  return { datePart, timePart };
};

export const formatDateToLongString = (isoDateString: string): string => {
  const date = new Date(isoDateString);

  const day = date.getUTCDate();
  const month = date.toLocaleString('en-uk', { month: 'long', timeZone: 'UTC' });
  const year = date.getUTCFullYear();

  return `${day} ${month} ${year}`;
};

/**
 * Formats a Date object to YYYY-MM-DD string without timezone conversion.
 * This ensures the local date is preserved and not affected by timezone shifts.
 *
 * @param {Date} date - The date to format
 * @returns {string} The date in YYYY-MM-DD format
 */
export const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Calculates a date a certain number of months in the past from today.
 * @param {number} [monthsToSubtract=3] - The number of months to go back. Defaults to 3.
 * @returns {Date} The resulting Date object.
 */
export const getDateMinusMonths = (monthsToSubtract = 3): Date => {
  const today = new Date();
  return new Date(today.setMonth(today.getMonth() - monthsToSubtract));
};

/**
 * Calculates a date a certain number of years in the past from today.
 * @param {number} [yearsToSubtract=10] - The number of years to go back. Defaults to 10.
 * @returns {Date} The resulting Date object.
 */
export const getDateMinusYears = (yearsToSubtract = 10): Date => {
  const today = new Date();
  return new Date(today.setFullYear(today.getFullYear() - yearsToSubtract));
};

/**
 * Calculates a date a certain number of years in the future from today.
 * @param {number} [yearsToAdd=10] - The number of years to add. Defaults to 10.
 * @returns {Date} The resulting Date object.
 */
export const getDatePlusYears = (yearsToAdd = 10): Date => {
  const today = new Date();
  return new Date(today.setFullYear(today.getFullYear() + yearsToAdd));
};

/**
 * Returns a new Date object representing the local midnight (00:00:00)
 * of the given date, ignoring the time component.
 * @param {Date} date - The date to normalize to local midnight.
 * @returns {Date} A new Date object at local midnight for the provided date.
 */
const getLocalMidnight = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

/**
 * Checks whether two dates fall on the same calendar day in the user's local timezone.
 *
 * @description
 * Compares the local year/month/day only, ignoring the time component. Use this rather than
 * comparing ISO strings or timestamps when the question is "is this the same day the user
 * sees?" — two instants a few hours apart can share a local day but differ in UTC (and
 * vice versa).
 *
 * @param {Date} a - The first date to compare.
 * @param {Date} b - The second date to compare.
 * @returns {boolean} True when both dates are on the same local calendar day.
 */
export const isSameLocalDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * @typedef {Object} DueDateInfo
 * @property {string} text - The text to display for the date.
 * @property {string} style - The Tailwind CSS class string for styling the text.
 * @property {boolean} showIcon - Whether to show the associated icon.
 */

/**
 * Analyses a due date and returns styling and text information based on its proximity to today.
 *
 * Rules:
 * - Date is today or in the past: Red text, icon is visible, shows formatted date.
 * - Date is 1 day in the future: Red text, icon is visible, shows "Today".
 * - Date is 2-7 days in the future: 'X days remaining', red text.
 * - Date is 8-30 days in the future: 'X days remaining', black text.
 * - Date is >30 days in the future: Black text, shows formatted date.
 *
 * @param {string | null | undefined} dueDateString - The ISO date string (e.g., '2025-08-31T00:00:01Z').
 * @param {function} dateFormatter - A function that takes the date string and formats it for display.
 * @param {boolean} formatOnly - If true, only returns the formatted date without styling or icon logic.
 * @returns {DueDateInfo} An object with text, style, and showIcon properties.
 */
export const getDueDateStyling = (
  dueDateString: string,
  dateFormatter: typeof getDateAndTimestamp,
  formatOnly: boolean = false,
) => {
  // --- Default state for invalid input ---
  if (!dueDateString) {
    return { text: '-', style: 'text-black', showIcon: false };
  }

  if (formatOnly) {
    return {
      text: dateFormatter(dueDateString, false, false),
      style: 'text-black',
      showIcon: false,
    };
  }

  // --- Date Calculation ---
  // Normalize both dates to local midnight
  const today = getLocalMidnight(new Date());
  const dueDate = getLocalMidnight(new Date(dueDateString));

  const diffTime = dueDate.getTime() - today.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  // --- Logic branching ---
  // Rule 1: Date is in the past
  if (diffDays < 0) {
    return {
      text: dateFormatter(dueDateString, false, false),
      style: 'text-red-500',
      showIcon: true,
    };
  }

  // Rule 2: Date is today
  if (diffDays === 0) {
    return {
      text: 'Today',
      style: 'text-red-500',
      showIcon: true,
    };
  }

  // Rule 3: Date is 1-7 days in the future (1-7 days remaining)
  if (diffDays <= 7) {
    const dayText = diffDays === 1 ? 'day' : 'days';
    return {
      text: `${diffDays} ${dayText} remaining`,
      style: 'text-red-500',
      showIcon: false,
    };
  }

  // Rule 4: Date is 8-30 days in the future (8-30 days remaining)
  if (diffDays <= 30) {
    return {
      text: `${diffDays} days remaining`,
      style: 'text-black',
      showIcon: false,
    };
  }

  // Rule 5: Date is more than 30 days in the future
  return {
    text: dateFormatter(dueDateString, false, false),
    style: 'text-black',
    showIcon: false,
  };
};

export const formatTimeHHSS = (maybeDate: string) => {
  const date = new Date(maybeDate);

  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  };

  return date.toLocaleDateString('en-US', options).split(',')[1];
};

/**
 * Formats a date as `15 Jun 2026` — the app's convention for a date read as prose rather
 * than tabulated. Accepts a `Date` as well as an ISO string, for callers already holding
 * one (a picker's value, a parsed range) that would otherwise round-trip through a string.
 */
export const formatDateDDMMMYYYY = (inputDate: string | Date): string => {
  const date = new Date(inputDate);

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  };

  return date.toLocaleDateString('en-GB', options);
};

export const shiftDateToEquivalentUtc = (date: Date): Date => {
  let offset = date.getTimezoneOffset();

  if (offset === 0) {
    return date;
  }

  if (offset < 0) {
    offset += Math.abs(offset) * 2;
  }

  const userTimezoneOffset = offset * 60_000;
  const equivalentUtcTime = new Date(date.getTime() + userTimezoneOffset);

  return equivalentUtcTime;
};

export const convertDateToSerializableString = (
  dateArray: [Date, Date | null] | null | undefined,
): string | undefined => {
  if (dateArray === null || dateArray === undefined) {
    return undefined;
  }

  let dateString = '';

  const date1 = dateArray[0];
  const date2 = dateArray[1];

  if (date1) {
    dateString = shiftDateToEquivalentUtc(date1).toISOString().split('T')[0] ?? '';
  }

  if (date2) {
    dateString = `${dateString}_${shiftDateToEquivalentUtc(date2).toISOString().split('T')[0] ?? ''}`;
  }

  return dateString;
};

/**
 * Converts a date range selection to ISO string format with proper timezone handling.
 *
 * @description
 * This function takes an array of Date objects (typically from a date range picker)
 * and converts them to ISO strings suitable for API requests. The start date is set
 * to the beginning of the day, and the end date is set to 23:59:59.999 (end of day).
 * If the dates are in reverse order, they are automatically swapped.
 *
 * @param {Date[] | null | undefined} selection - Array containing [startDate, endDate]
 * @returns {{ startDate: string | null; endDate: string | null }} Object with ISO formatted date strings
 */
export const setDateRangeFilter = (
  selection: Date[] | null | undefined,
): { startDate: string | null; endDate: string | null } => {
  let startDate: string | null = null;
  let endDate: string | null = null;

  if (selection) {
    if (selection[0]) {
      startDate = convertToISOWithTimezone(selection[0]);
    }
    if (selection[1]) {
      endDate = convertToISOWithTimezone(selection[1], false, true);
    }

    // If startDate is after endDate, swap them
    if (startDate && endDate && !dateIsAfterStartDate(endDate, startDate)) {
      const temp = startDate;
      startDate = endDate;
      endDate = temp;
    }
  }

  return {
    startDate,
    endDate,
  };
};

/**
 * The zone the platform's calendar day is read in. South Africa keeps no daylight saving, so this
 * is always UTC + 2 h, and the backend reads the same day (DL-75).
 */
export const PLATFORM_TIME_ZONE = 'Africa/Johannesburg';

// Built once: constructing an Intl formatter is far slower than calling one, and these run per row.
const platformDayParts = new Intl.DateTimeFormat('en-GB', {
  timeZone: PLATFORM_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const platformDayDisplay = new Intl.DateTimeFormat('en-GB', {
  timeZone: PLATFORM_TIME_ZONE,
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const isValidInstant = (instant: Date | number): boolean =>
  !Number.isNaN(new Date(instant).getTime());

/**
 * The instant's calendar day in the platform's zone as `YYYY-MM-DD`, so two days compare as
 * strings. Never the browser's zone: the answer is the same wherever the browser runs. An invalid
 * instant gives `''`, which is not a day, rather than the `RangeError` Intl would throw.
 */
export const platformDayKey = (instant: Date | number): string => {
  if (!isValidInstant(instant)) return '';
  const parts = platformDayParts.formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year').padStart(4, '0')}-${part('month')}-${part('day')}`;
};

/**
 * The instant's calendar day in the platform's zone as `DD Mmm YYYY`, as the site log's date rows
 * read (`en-GB`, so September prints "Sept"). An invalid instant gives `''` and never throws.
 */
export const formatPlatformDay = (instant: Date | number): string =>
  isValidInstant(instant) ? platformDayDisplay.format(instant) : '';

// Reads a stored site date as `new Date(date)` always has here: null is the epoch, and an empty or
// unparsable string is no day at all.
export const siteDayKey = (date: string | null | undefined): string => {
  if (date === undefined) return '';
  if (date === null) return platformDayKey(0);
  return platformDayKey(Date.parse(date));
};

const SITE_DAY_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * A stored site date as its platform day, `D Mmm YYYY` ("10 Oct 2026"), the shape
 * `getDateAndTimestamp` prints. Context: the site form stores a start as SAST midnight, the UTC day
 * before, so reading the browser's day (or the string's first ten characters) can name the day
 * before. An unusable date reads "Invalid date", as `getDateAndTimestamp` does.
 */
export const formatSiteDay = (date: string | null | undefined): string => {
  const key = siteDayKey(date);
  if (!key) return 'Invalid date';
  const [year, month, day] = key.split('-').map(Number);
  return `${day} ${SITE_DAY_MONTHS[month! - 1]} ${year}`;
};

/**
 * Whether approving today falls outside the site's dates, judged by whole calendar days in the
 * platform's zone, so the site's last day counts as inside (FR-ST9).
 *
 * Context: a missing date keeps the result the hosts gave before this helper. A `null` date reads
 * as 1 Jan 1970, so a null end puts every day outside; an empty, missing or unparsable date is
 * ignored, and an invalid `today` approves with no dialog.
 */
export const isOutsideSiteDates = (
  site: { startDate?: string | null; endDate?: string | null },
  today: Date,
): boolean => {
  const todayKey = platformDayKey(today);
  if (!todayKey) return false;

  const startKey = siteDayKey(site.startDate);
  const endKey = siteDayKey(site.endDate);
  return (startKey !== '' && todayKey < startKey) || (endKey !== '' && todayKey > endKey);
};
